var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
import React, { useCallback, useState } from "react";
import DynamicTable from "@atlaskit/dynamic-table";
import Lozenge from "@atlaskit/lozenge";
import Toggle from "@atlaskit/toggle";
import { useMutationUI, usePollingQuery } from "../../hooks";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from "@atlaskit/modal-dialog";
import Button, { LoadingButton } from "@atlaskit/button";
import styled from "styled-components";
import { CronosConfigForServiceQuery, DisableCronosConfigMutation, EnableCronosConfigMutation, DeferCronosConfigMutation, ResilienceFaults } from "../../graphql";
import { ConfigDetailsEditModal } from "../FormView/CronosConfigEditForm";
import Tooltip from "@atlaskit/tooltip";
import { isAZFailureNear, isConfigDisabled, isFailaConfig } from "../../utils/cronos";
import SectionMessage from "@atlaskit/section-message";
import { isEmpty } from "lodash";
import { BoldSpan, ExpandablePanel } from "@atlassiansox/microscopekit";
import { CentreAlignDiv, DynamicTableWrapper } from "../../utils/styles";
var getMessageForFailaConfig = function (config) {
    return isFailaConfig(config)
        ? "UI editing is disabled. Your service currently has a Faila resource configured via SD, please refer go/continuous-chaos-runbook."
        : "Click to enable/disable the configuration";
};
var getMessageForAzFailure = function (config) {
    if (config.nextTrigger === "Excluded") {
        return config.service + " in " + config.environment + " is excluded from AZ Failure execution";
    }
    else if (isAZFailureNear(config)) {
        return "Skip AZ Failure execution for next 7 days";
    }
    else {
        return "Next AZ Failure execution is more than 7 days away. Can't defer now";
    }
};
var configToolTipMessage = function (config) {
    var messages = {
        FailaConfig: getMessageForFailaConfig(config),
        AzFailureMessage: getMessageForAzFailure(config)
    };
    if (config.faultType === ResilienceFaults.terminate_instances) {
        return messages.FailaConfig;
    }
    else if (config.faultType === ResilienceFaults.az_failure) {
        return messages.AzFailureMessage;
    }
};
var head = {
    cells: [
        {
            key: "micros-env",
            content: "Micros Env",
            isSortable: true
        },
        {
            key: "fault-type",
            content: "Fault Type",
            isSortable: true
        },
        {
            key: "next-trigger",
            content: "Next Trigger(UTC)",
            isSortable: true
        },
        {
            key: "configuration",
            content: "Configuration",
            isSortable: true
        },
        {
            key: "status",
            content: "Status",
            isSortable: true
        }
    ]
};
export var CronosConfigsListView = function (props) {
    var _a;
    var _b = usePollingQuery(CronosConfigForServiceQuery, {
        variables: { serviceId: props.serviceId }
    }), loading = _b.loading, data = _b.data;
    var configs = (_a = data === null || data === void 0 ? void 0 : data.cronosConfigForService) !== null && _a !== void 0 ? _a : [];
    return (React.createElement("div", null,
        React.createElement("br", null),
        React.createElement(CronosConfigsDynamicTable, { configs: configs, serviceId: props.serviceId, isDataLoading: loading })));
};
var CronosConfigsDynamicTable = function (_a) {
    var configs = _a.configs, isDataLoading = _a.isDataLoading, serviceId = _a.serviceId;
    var _b = __read(useState(false), 2), isEditOpen = _b[0], setIsEditOpen = _b[1];
    var _c = __read(useState(false), 2), isStatusUpdateOpen = _c[0], setIsStatusUpdateOpen = _c[1];
    var openEditModal = useCallback(function () { return setIsEditOpen(true); }, []);
    var getUpdateStatus = function () {
        return (configDetails === null || configDetails === void 0 ? void 0 : configDetails.status) === "enabled" ? "disable" : "enable";
    };
    var openStatusUpdateModal = useCallback(function () { return setIsStatusUpdateOpen(true); }, []);
    var closeStatusUpdateModal = useCallback(function () { return setIsStatusUpdateOpen(false); }, []);
    var _d = __read(useState(), 2), configDetails = _d[0], setConfigDetails = _d[1];
    var _e = useMutationUI({
        mutation: (configDetails === null || configDetails === void 0 ? void 0 : configDetails.status) === "enabled"
            ? DisableCronosConfigMutation
            : EnableCronosConfigMutation,
        successMessage: "config is queued to be " + getUpdateStatus() + ", This usually takes upto 30 seconds",
        options: {
            variables: {
                id: configDetails === null || configDetails === void 0 ? void 0 : configDetails.id
            },
            onCompleted: function () {
                closeStatusUpdateModal();
            },
            onError: function () {
                closeStatusUpdateModal();
            }
        }
    }), mutateEditStatus = _e.mutate, isLoading = _e.isLoading;
    var _f = useMutationUI({
        mutation: DeferCronosConfigMutation,
        successMessage: "AZ Failure execution has been deferred successfully.\n      AZ Failure execution for " + (configDetails === null || configDetails === void 0 ? void 0 : configDetails.service) + " in " + (configDetails === null || configDetails === void 0 ? void 0 : configDetails.environment) + " will now happen on " + (configDetails === null || configDetails === void 0 ? void 0 : configDetails.nextTrigger) + " UTC",
        options: {
            variables: {
                configID: configDetails === null || configDetails === void 0 ? void 0 : configDetails.id
            },
            onCompleted: function () {
                closeStatusUpdateModal();
            },
            onError: function () {
                closeStatusUpdateModal();
            }
        }
    }), deferAzExecution = _f.mutate, isDeferAzExecutionLoading = _f.isLoading;
    var faultTypes = __spread(new Set(configs.map(function (item) { return item.faultType; })));
    var getTitle = function (faultType) {
        var _a;
        var faultMap = {
            az_failure: "AZ Failure",
            terminate_instances: "Terminate Instances"
        };
        return (_a = faultMap[faultType]) !== null && _a !== void 0 ? _a : faultType;
    };
    var getDialogContent = function (config) {
        var content = {
            TerminateInstanceDefault: (React.createElement(React.Fragment, null,
                "Do you wish to continue? This will",
                " ",
                React.createElement(Lozenge, { appearance: "new" }, getUpdateStatus()),
                " the terminate instance config in ", config === null || config === void 0 ? void 0 :
                config.environment)),
            AZFailureDefault: (React.createElement(React.Fragment, null,
                "Do you wish to ",
                React.createElement(Lozenge, { appearance: "new" }, "Defer"),
                "? This will",
                " ",
                "ensure that there is no az failure execution in next 7 days in",
                " ", config === null || config === void 0 ? void 0 :
                config.environment,
                ".",
                React.createElement("br", null),
                React.createElement("br", null),
                React.createElement(BoldSpan, null, "Executions can be deferred only twice per quarter.")))
        };
        return (config === null || config === void 0 ? void 0 : config.faultType) === ResilienceFaults.terminate_instances
            ? content.TerminateInstanceDefault
            : content.AZFailureDefault;
    };
    var _g = __read(useState({}), 2), collapsedStates = _g[0], setCollapsedStates = _g[1];
    var handleToggle = function (faultType) {
        setCollapsedStates(function (prevState) {
            var _a;
            return (__assign(__assign({}, prevState), (_a = {}, _a[faultType] = !prevState[faultType], _a)));
        });
    };
    var onSubmit = function () {
        (configDetails === null || configDetails === void 0 ? void 0 : configDetails.faultType) === ResilienceFaults.terminate_instances
            ? mutateEditStatus()
            : deferAzExecution();
    };
    return (React.createElement(Container, null,
        React.createElement(ModalTransition, null, isStatusUpdateOpen && (React.createElement(Modal, { onClose: closeStatusUpdateModal },
            React.createElement(ModalHeader, null,
                React.createElement(ModalTitle, null, "Update Config Status")),
            React.createElement(ModalBody, null, getDialogContent(configDetails)),
            React.createElement(ModalFooter, null,
                React.createElement(Button, { appearance: "subtle", onClick: closeStatusUpdateModal }, "Cancel"),
                React.createElement(LoadingButton, { appearance: "primary", isLoading: isLoading || isDeferAzExecutionLoading, onClick: function () {
                        onSubmit();
                    } }, "Continue"))))),
        React.createElement(ConfigDetailsEditModal, { isOpen: isEditOpen, configDetails: configDetails, setIsOpen: setIsEditOpen }),
        isEmpty(configs) && !isDataLoading && (React.createElement(SectionMessage, { appearance: "change" },
            "Your service isn't onboarded to continuous-chaos or migrated from Faila yet. Please refer this\u00A0",
            React.createElement("a", { href: "https://go.atlassian.com/faila-cc-migration", target: "_blank", rel: "noopener noreferrer" }, "page"),
            "\u00A0for more details.")),
        React.createElement("h3", null, "Continuous Chaos Configurations"),
        React.createElement("br", null),
        configs.length > 0 ? (React.createElement(React.Fragment, null, faultTypes.map(function (faultType) {
            var _a;
            return (React.createElement(ExpandablePanel, { isCollapsed: collapsedStates[faultType], title: getTitle(faultType) + " Configurations", onToggle: function () { return handleToggle(faultType); } },
                React.createElement(DynamicTableWrapper, null,
                    React.createElement(DynamicTable, { isLoading: isDataLoading, head: head, isFixedSize: true, defaultSortKey: "micros-env", defaultSortOrder: "ASC", rows: ((_a = configs.filter(function (config) { return config.faultType === faultType; })) !== null && _a !== void 0 ? _a : []).map(function (config) { return ({
                            key: "row-" + config.id,
                            cells: [
                                {
                                    key: "environment-" + config.environment,
                                    content: config.environment
                                },
                                {
                                    key: "fault-type-" + config.faultType,
                                    content: config.faultType
                                },
                                {
                                    key: "next-trigger-" + config.id,
                                    content: config.nextTrigger
                                        ? config.nextTrigger
                                        : "random"
                                },
                                {
                                    key: "config-parameters-" + config.id,
                                    content: (React.createElement(Button, { appearance: "link", onClick: function (e) {
                                            setConfigDetails(config);
                                            openEditModal();
                                        } }, "Details"))
                                },
                                {
                                    key: "config-status-" + config.status,
                                    content: (React.createElement(Tooltip, { content: configToolTipMessage(config) },
                                        config.faultType ===
                                            ResilienceFaults.terminate_instances && (React.createElement(Container, null,
                                            React.createElement(Toggle, { id: "toggle-" + config.id, isChecked: config.status === "enabled", isDisabled: isConfigDisabled(config), onChange: function (e) {
                                                    setConfigDetails(config);
                                                    openStatusUpdateModal();
                                                } }))),
                                        config.faultType ===
                                            ResilienceFaults.az_failure && (React.createElement(Button, { appearance: "primary", spacing: "compact", isDisabled: !isAZFailureNear(config), onClick: function () {
                                                setConfigDetails(config);
                                                openStatusUpdateModal();
                                            } }, "Defer"))))
                                }
                            ]
                        }); }), emptyView: React.createElement("div", null, "No Configurations Found"), rowsPerPage: 5 }))));
        }))) : (React.createElement(CentreAlignDiv, null, "No configs to display"))));
};
export var Container = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  margin-top: 10px;\n"], ["\n  margin-top: 10px;\n"])));
var templateObject_1;
